import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAmplitude } from './AmplitudeProvider';
import { selectors as variantsSelectors } from './variantsSlice';

import type { RootState } from './variantsSlice';

export function useFeatureFlag(
    flagName: string,
    defaultValue = false,
    variantName = 'on',
) {
    const { experiment } = useAmplitude();
    const hasFetched = useSelector(variantsSelectors.selectHasFetched);
    const storedVariant = useSelector((state: RootState) =>
        variantsSelectors.selectById(state, flagName),
    );

    // Resolve the flag synchronously during render so SSR (and the hydration
    // render) produce the correct output. On non-cached pages, the server
    // dispatches `variantsReceived` before rendering, so `hasFetched` is true
    // and the variant is already in the store.
    const [isEnabled, setIsEnabled] = useState(() =>
        (hasFetched ? storedVariant?.value === variantName : defaultValue),
    );
    const [isLoading, setIsLoading] = useState(() => !hasFetched);

    useEffect(() => {
        if (!experiment || !hasFetched) {
            return;
        }

        setIsLoading(false);

        // Gate on the Redux store, not the SDK's experiment.variant().
        // The SDK falls back to localStorage when initialVariants is empty
        // and fetch() fails (ad blocker, timeout), returning stale values
        // that were never assigned this session. Redux is only populated
        // by successful fetches, so it accurately reflects whether an
        // assignment exists.
        if (storedVariant?.key) {
            experiment.exposure(flagName);
        }

        setIsEnabled(storedVariant?.value === variantName);
        // defaultValue is intentionally not in the dep array as it's being used by useState(defaultValue)
    }, [experiment, flagName, hasFetched, storedVariant, variantName]);

    return { isEnabled, isLoading };
}
